'use client;';

import { createContext, useContext } from 'react';

import { FloatingActionButtons } from '@/components/molecules/floating-action-buttons';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { useEditableStateUpdates } from '@/hooks/use-editable-state';

function transform(v: ApiSchemas['OfficeReadComplete']) {
  return {
    ...v,
    location: v.location as ApiSchemas['LocationUpsert'] | null | undefined,
    settings_scheduling: v.settings_scheduling as ApiSchemas['OfficeSettingsSchedulingUpsert'] | null | undefined,
    office_hours: v.office_hours?.map((v) => ({
      id: v.id,
      day_of_week: v.day_of_week,
      start_time: v.start_time,
      end_time: v.end_time,
    })),
  };
}

type OfficeInState = ReturnType<typeof transform>;

interface ContextType {
  office: OfficeInState;
  update: (partial: Partial<OfficeInState>) => void;
}

const OfficeEditingContext = createContext<ContextType | null>(null);

export function useOfficeEditingCtx() {
  const ctx = useContext(OfficeEditingContext);
  if (!ctx) {
    throw new Error('useOfficeEditingCtx must be used within a OfficeEditingProvider');
  }
  return ctx;
}

interface Props {
  children: React.ReactNode;
}

export function OfficeEditingProvider({ children }: Props) {
  const { api } = useApiV1Ctx();
  const { office, refetch } = useOfficeCtx();
  const state = useEditableStateUpdates(office, transform);
  const updateMutation = api.offices.updateOfficesOfficeIdPatch.useMutation(
    {
      path: { office_id: office.id },
    },
    {
      onSuccess: (data) => {
        state.reset(data);
        refetch();
      },
    }
  );

  const onSave = () => {
    updateMutation.mutate(state.updates);
  };

  return (
    <OfficeEditingContext.Provider value={{ office: state.value, update: state.setUpdates }}>
      {children}
      {state.isDirty && (
        <FloatingActionButtons
          onSave={onSave}
          onDiscard={() => state.reset(office)}
          disabled={updateMutation.isPending}
        />
      )}
    </OfficeEditingContext.Provider>
  );
}
